<p-toast [style]="{marginTop: '80px'}"></p-toast>
<p-confirmDialog header="Confirm Delete" icon="pi pi-exclamation-triangle"></p-confirmDialog>

<!-- Loading -->
@if (loading) {
  <div class="loading-container">
    <p-progressSpinner></p-progressSpinner>
    <p>Loading delete preview...</p>
  </div>
}

<!-- Error -->
@if (error && !loading) {
  <div class="error-container">
    <p-message severity="error" [text]="error"></p-message>
    <br>
      <button pButton label="Back to Properties" icon="pi pi-arrow-left" class="p-button-secondary" (click)="onCancel()"></button>
    </div>
  }

  <!-- Preview Content -->
  @if (preview && !loading) {
    <div class="delete-preview-container">
      <!-- Page Header -->
      <div class="page-header">
        <h2><i class="pi pi-trash" style="color: #d32f2f;"></i> Delete Property: {{preview.propertyCode}} (ID: {{shortTermRentalId}})</h2>
        <span class="progress-label">Progress: {{getCompletedStepCount()}} / {{deleteSteps.length}} steps complete</span>
      </div>
      <!-- Property Info -->
      <p-card header="Property Information" styleClass="property-info-card">
        <div class="property-info-grid">
          <div><strong>Property Code:</strong> {{preview.propertyCode}}</div>
          <div><strong>Property Name:</strong> {{preview.propertyName}}</div>
          <div><strong>Listing Status:</strong>
          <span [ngClass]="{'status-active': preview.listingStatus === 0, 'status-suspended': preview.listingStatus === 2, 'status-canceled': preview.listingStatus === 1}">
            {{preview.listingStatus === 0 ? 'Active' : preview.listingStatus === 2 ? 'Suspended' : 'Canceled'}}
          </span>
        </div>
        <div><strong>Total Child Records:</strong> <span class="total-count">{{getTotalChildRecords()}}</span></div>
      </div>
    </p-card>
    <!-- Blocked Banner -->
    @if (deleteBlocked) {
      <div class="blocked-banner">
        <p-message severity="error"
          [text]="'DELETE BLOCKED: ' + futureConfirmedReservations + ' confirmed reservation(s) with future check-out date(s). Cancel or close these reservations before deleting this property.'">
        </p-message>
      </div>
    }
    <!-- Warnings -->
    <div class="warnings-section">
      @if (!deleteBlocked) {
        <p-message severity="warn" text="This action is PERMANENT and cannot be undone. Delete each category step-by-step below."></p-message>
      }
      @for (warning of warnings; track warning) {
        <p-message severity="warn" [text]="warning" styleClass="warning-item"></p-message>
      }
    </div>
    <!-- One-Click Delete -->
    @if (!deleteBlocked) {
      <p-card header="One-Click Delete (Recommended)" styleClass="steps-card">
        <div style="display: flex; align-items: center; gap: 16px; flex-wrap: wrap;">
          <button pButton
                  icon="pi pi-trash"
                  label="Delete Property Now"
                  class="p-button-danger p-button-lg"
                  (click)="onOneClickDelete()"
                  [disabled]="oneClickDeleting || isAnyStepDeleting() || getCompletedStepCount() === deleteSteps.length">
          </button>
          <span style="color: #555;">
            Runs all {{deleteSteps.length}} delete steps in order with two confirmations. Use the step-by-step table below only if you need granular control.
          </span>
        </div>
      </p-card>
    }

    <!-- Step-by-Step Delete Table -->
    <p-card header="Delete Steps (Manual / Step-by-Step)" styleClass="steps-card">
      <p-table [value]="deleteSteps" [paginator]="false" styleClass="p-datatable-sm p-datatable-striped">
        <ng-template pTemplate="header">
          <tr>
            <th style="width: 50px;">#</th>
            <th>Category</th>
            <th style="text-align: right; width: 120px;">Records</th>
            <th style="width: 140px;">Status</th>
            <th style="width: 140px;">Action</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-step let-i="rowIndex">
          <tr [ngClass]="{'step-done': step.status === 'done', 'step-error': step.status === 'error', 'step-deleting': step.status === 'deleting'}">
            <td>{{i + 1}}</td>
            <td>
              {{step.label}}
              @if (step.errorMessage) {
                <div class="step-error-msg">
                  <small><i class="pi pi-exclamation-circle"></i> {{step.errorMessage}}</small>
                </div>
              }
            </td>
            <td style="text-align: right; font-weight: bold;">
              @if (step.key !== 'property') {
                <span>{{getStepCount(step)}}</span>
              }
              @if (step.key === 'property') {
                <span>1</span>
              }
            </td>
            <td>
              @if (step.status === 'done') {
                <p-tag severity="success" value="Deleted" icon="pi pi-check"></p-tag>
              }
              @if (step.status === 'pending') {
                <p-tag severity="info" value="Pending"></p-tag>
              }
              @if (step.status === 'deleting') {
                <p-tag severity="warning" value="Deleting..." icon="pi pi-spin pi-spinner"></p-tag>
              }
              @if (step.status === 'skipped') {
                <p-tag value="No Records"></p-tag>
              }
              @if (step.status === 'error') {
                <p-tag severity="danger" value="Error" icon="pi pi-times"></p-tag>
              }
            </td>
            <td>
              <button pButton icon="pi pi-trash" class="p-button-danger p-button-sm"
                label="Delete" (click)="onDeleteStep(step)"
                [disabled]="!canDeleteStep(i) || isAnyStepDeleting()">
              </button>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </p-card>
    <!-- Reservation Details -->
    @if (reservationDetails.length > 0) {
      <p-card header="Reservation Details (Will be deleted in Step 1)" styleClass="reservation-details-card">
        <p-table [value]="reservationDetails" [paginator]="reservationDetails.length > 10" [rows]="10"
          styleClass="p-datatable-sm p-datatable-striped">
          <ng-template pTemplate="header">
            <tr>
              <th>Reservation ID</th>
              <th>Guest Name</th>
              <th>Check-In</th>
              <th>Check-Out</th>
              <th>Status</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-res>
            <tr>
              <td>{{res.reservationId}}</td>
              <td>{{res.firstName}} {{res.lastName}}</td>
              <td>{{res.checkInDateTime | date:'MM/dd/yyyy'}}</td>
              <td>{{res.checkOutDateTime | date:'MM/dd/yyyy'}}</td>
              <td>{{res.reservationStatus}}</td>
            </tr>
          </ng-template>
        </p-table>
      </p-card>
    }
    <!-- Back Button -->
    <div class="action-buttons">
      <button pButton label="Back to Properties" icon="pi pi-arrow-left" class="p-button-secondary" (click)="onCancel()" [disabled]="isAnyStepDeleting()"></button>
    </div>
  </div>
}
